<script setup lang="ts">
import { ref, computed } from "vue";
import MarkdownIt from "markdown-it";
import guideMd from "@/docs/guide.md?raw";

// Resolve bundled image URLs so the relative ./images/* paths inside guide.md
// point at the hashed assets Vite emits (instead of breaking at runtime).
const imageMap = import.meta.glob("@/docs/images/*", {
    eager: true,
    query: "?url",
    import: "default",
}) as Record<string, string>;

function resolveImage(src: string): string {
    const name = src.replace(/^\.\//, "").replace(/^images\//, "");
    for (const [path, url] of Object.entries(imageMap)) {
        if (path.endsWith("/" + name)) return url;
    }
    return src;
}

const md = new MarkdownIt({ html: false, linkify: true, breaks: false });
const defaultImageRender =
    md.renderer.rules.image ??
    ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const aIndex = token.attrIndex("src");
    if (aIndex >= 0 && token.attrs) {
        token.attrs[aIndex][1] = resolveImage(token.attrs[aIndex][1]);
    }
    return defaultImageRender(tokens, idx, options, env, self);
};

const html = computed(() => md.render(guideMd));
const open = ref(false);
</script>

<template>
    <v-tooltip>
        <template #activator="{ props }">
            <v-btn
                v-bind="props"
                @click="open = true"
                color="primary"
                size="small"
                prepend-icon="mdi-book-open-variant"
                class="ml-1"
                >使用說明</v-btn
            >
        </template>
        開啟使用說明
    </v-tooltip>

    <v-dialog v-model="open" max-width="960" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center">
                <span>使用說明</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" @click="open = false" />
            </v-card-title>
            <v-divider />
            <v-card-text>
                <div class="user-guide-content" v-html="html" />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.user-guide-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 8px 0;
    display: block;
}
.user-guide-content :deep(h2) {
    margin-top: 20px;
}
.user-guide-content :deep(blockquote) {
    border-left: 3px solid rgba(127, 127, 127, 0.4);
    padding-left: 12px;
    margin: 8px 0;
    opacity: 0.85;
}
.user-guide-content :deep(code) {
    background: rgba(127, 127, 127, 0.15);
    padding: 1px 4px;
    border-radius: 3px;
}
</style>
